// frontend/src/layouts/MainLayout.js
import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import useAuthStore from '../store/authStore';
import useWalletStore from '../store/walletStore';
import useNotificationsStore from '../store/notificationsStore';
import {
  Home,
  Wallet,
  Smartphone,
  Wifi,
  Zap,
  Users,
  User,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Shield,
  HelpCircle,
  Mail,
  Phone,
  MapPin,
  MessageCircle,
  FileText,
  ShieldCheck,
  Store,
  Bell,
  BellOff, 
  Heart, 
  AtSign, 
  ShoppingBag, 
  ArrowUpRight, 
  ArrowDownRight 
} from 'lucide-react';

const MainLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const { user, logout } = useAuthStore();
  const { wallet } = useWalletStore();
  const { 
    notifications, 
    unreadCount, 
    isLoading, 
    getNotifications, 
    getUnreadCount, 
    markAsRead, 
    markAllAsRead,
    deleteNotification,
    clearAllNotifications
  } = useNotificationsStore();
  const navigate = useNavigate();

  // Load notifications and unread count on mount
  useEffect(() => {
    if (user) {
      getUnreadCount();
      getNotifications({ limit: 10 });
    }
  }, [user]);

  // Fetch unread count periodically
  useEffect(() => {
    if (!user) return;
    
    const interval = setInterval(() => {
      getUnreadCount();
    }, 30000); // Every 30 seconds

    return () => clearInterval(interval);
  }, [user]);

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: Home },
    { name: 'Wallet', href: '/wallet', icon: Wallet },
    { name: 'Services', href: '/services', icon: Zap },
    { name: 'Marketplace', href: '/marketplace', icon: Store },
    { name: 'FastConnect', href: '/community', icon: Users },
    { name: 'Profile', href: '/profile', icon: User },
    { name: 'Settings', href: '/settings', icon: Settings },
  ];

  // Add Admin link if user is admin
  if (user?.role === 'ADMIN') {
    navigation.push({ name: 'Admin', href: '/admin', icon: Shield });
  }

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const getUserInitials = () => {
    if (!user) return 'U';
    return `${user.firstName?.charAt(0) || ''}${user.lastName?.charAt(0) || ''}`;
  };

  // FIXED: Handle notification click without flickering
  const handleNotificationClick = async (notification) => {
    // Close the dropdown immediately to prevent flickering
    setNotificationsOpen(false);
    
    // Mark as read in the background (don't await it)
    if (!notification.read) {
      markAsRead(notification.id).catch(err => {
        console.error('Failed to mark notification as read:', err);
      });
    }
    
    // Navigate based on notification type after a small delay
    // This ensures the dropdown is closed before navigation
    setTimeout(() => {
      if (notification.data?.postId) {
        navigate(`/community/post/${notification.data.postId}`);
      } else if (notification.data?.orderId) {
        navigate(`/marketplace/orders`);
      } else if (notification.data?.businessId) {
        navigate(`/community/marketplace/business/${notification.data.businessId}`);
      } else {
        // Default: just close the dropdown
        setNotificationsOpen(false);
      }
    }, 50);
  };

  const handleNotificationDelete = (e, notificationId) => {
    e.stopPropagation();
    deleteNotification(notificationId);
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'NEW_FOLLOWER':
        return <Users className="h-4 w-4 text-blue-500" />;
      case 'POST_LIKE':
        return <Heart className="h-4 w-4 text-red-500" />;
      case 'POST_COMMENT':
        return <MessageCircle className="h-4 w-4 text-green-500" />;
      case 'NEW_MESSAGE':
        return <Mail className="h-4 w-4 text-purple-500" />;
      case 'MENTION':
        return <AtSign className="h-4 w-4 text-yellow-500" />;
      case 'ORDER_CREATED':
      case 'ORDER_UPDATED':
      case 'ORDER_COMPLETED':
        return <ShoppingBag className="h-4 w-4 text-orange-500" />;
      case 'WALLET_CREDITED':
        return <ArrowUpRight className="h-4 w-4 text-green-500" />;
      case 'WALLET_DEBITED':
        return <ArrowDownRight className="h-4 w-4 text-red-500" />;
      case 'SYSTEM':
        return <Bell className="h-4 w-4 text-gray-500" />;
      default:
        return <Bell className="h-4 w-4 text-gray-400" />;
    }
  };

  const getTimeAgo = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const UserAvatar = ({ size = 'md', className = '' }) => {
    const sizeClasses = {
      sm: 'w-8 h-8',
      md: 'w-10 h-10',
      lg: 'w-12 h-12',
      xl: 'w-32 h-32'
    };

    const textSizes = {
      sm: 'text-sm',
      md: 'text-base',
      lg: 'text-lg',
      xl: 'text-4xl'
    };

    if (user?.avatar) {
      return (
        <div className={`${sizeClasses[size]} rounded-lg overflow-hidden ${className}`}>
          <img
            src={user.avatar}
            alt={`${user.firstName} ${user.lastName}`}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.onerror = null;
              e.target.style.display = 'none';
              e.target.parentElement.innerHTML = `
                <div class="w-full h-full bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center">
                  <span class="text-white font-medium ${textSizes[size]}">${getUserInitials()}</span>
                </div>
              `;
            }}
          />
        </div>
      );
    }

    return (
      <div className={`${sizeClasses[size]} rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 shadow-md shadow-primary-500/30 flex items-center justify-center ${className}`}>
        <span className={`text-white font-medium ${textSizes[size]}`}>
          {getUserInitials()}
        </span>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-neumorph pb-16 md:pb-0">
      {/* Mobile sidebar */}
      {sidebarOpen && (
        <div className="md:hidden fixed inset-0 z-40 flex">
          <div
            className="fixed inset-0 bg-gray-600/50 backdrop-blur-sm"
            onClick={() => setSidebarOpen(false)}
          />
          <div className="relative flex-1 flex flex-col max-w-xs w-full bg-neumorph shadow-neomorph">
            <div className="absolute top-0 right-0 -mr-12 pt-2">
              <button
                className="ml-1 flex items-center justify-center h-10 w-10 rounded-lg bg-white shadow-neomorph hover:shadow-neomorph-hover transition-all text-gray-500"
                onClick={() => setSidebarOpen(false)}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
              <div className="flex-shrink-0 flex items-center px-4 mb-6">
                <div className="flex items-center space-x-2">
                  <img 
                    src="/fastel-logo.png" 
                    alt="Fastel" 
                    className="h-8 w-auto"
                  />
                  <span className="text-xl font-bold bg-gradient-to-r from-primary-700 via-primary-600 to-primary-400 bg-clip-text text-transparent">
                    Fastel
                  </span>
                </div>
              </div>
              <nav className="mt-5 px-3 space-y-1.5">
                {navigation.map((item) => (
                  <NavLink
                    key={item.name}
                    to={item.href}
                    className={({ isActive }) => {
                      const baseClasses = "group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all border";
                      const inactiveClasses = "text-gray-600 hover:bg-white hover:shadow-neomorph-hover border-transparent";
                      const activeClasses = item.name === 'Admin'
                        ? "bg-red-50 text-red-600 shadow-neomorph-inset border-red-100"
                        : "bg-primary-50 text-primary-600 shadow-neomorph-inset border-primary-100";
                      
                      return `${baseClasses} ${isActive ? activeClasses : inactiveClasses}`;
                    }}
                    onClick={() => setSidebarOpen(false)}
                  >
                    {({ isActive }) => (
                      <>
                        <div className={`p-1.5 rounded-md mr-3 transition-all ${
                          item.name === 'Admin' 
                            ? isActive ? 'bg-red-100' : 'bg-red-50'
                            : isActive ? 'bg-primary-100' : 'bg-primary-50'
                        }`}>
                          <item.icon className={`h-4 w-4 ${
                            item.name === 'Admin' ? 'text-red-600' : 'text-primary-600'
                          }`} />
                        </div>
                        <span className="flex-1">{item.name}</span>
                        {item.name === 'Admin' && (
                          <span className="ml-auto bg-red-100 text-red-600 text-xs px-2 py-0.5 rounded-full font-medium">
                            Admin
                          </span>
                        )}
                      </>
                    )}
                  </NavLink>
                ))}
                
                {/* Divider */}
                <div className="border-t border-primary-100/50 my-3" />
                
                {/* Support */}
                <NavLink
                  to="/support"
                  className={({ isActive }) => {
                    const baseClasses = "group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all border";
                    const inactiveClasses = "text-gray-600 hover:bg-white hover:shadow-neomorph-hover border-transparent";
                    const activeClasses = "bg-primary-50 text-primary-600 shadow-neomorph-inset border-primary-100";
                    return `${baseClasses} ${isActive ? activeClasses : inactiveClasses}`;
                  }}
                  onClick={() => setSidebarOpen(false)}
                >
                  {({ isActive }) => (
                    <>
                      <div className={`p-1.5 rounded-md mr-3 transition-all ${
                        isActive ? 'bg-primary-100' : 'bg-primary-50'
                      }`}>
                        <HelpCircle className={`h-4 w-4 ${isActive ? 'text-primary-600' : 'text-primary-600'}`} />
                      </div>
                      <span>Help & Support</span>
                    </>
                  )}
                </NavLink>

                {/* Legal Pages */}
                <NavLink
                  to="/privacy-policy"
                  className={({ isActive }) => {
                    const baseClasses = "group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all border";
                    const inactiveClasses = "text-gray-600 hover:bg-white hover:shadow-neomorph-hover border-transparent";
                    const activeClasses = "bg-primary-50 text-primary-600 shadow-neomorph-inset border-primary-100";
                    return `${baseClasses} ${isActive ? activeClasses : inactiveClasses}`;
                  }}
                  onClick={() => setSidebarOpen(false)}
                >
                  {({ isActive }) => (
                    <>
                      <div className={`p-1.5 rounded-md mr-3 transition-all ${
                        isActive ? 'bg-primary-100' : 'bg-primary-50'
                      }`}>
                        <FileText className={`h-4 w-4 ${isActive ? 'text-primary-600' : 'text-primary-600'}`} />
                      </div>
                      <span>Privacy Policy</span>
                    </>
                  )}
                </NavLink>

                <NavLink
                  to="/terms-of-service"
                  className={({ isActive }) => {
                    const baseClasses = "group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all border";
                    const inactiveClasses = "text-gray-600 hover:bg-white hover:shadow-neomorph-hover border-transparent";
                    const activeClasses = "bg-primary-50 text-primary-600 shadow-neomorph-inset border-primary-100";
                    return `${baseClasses} ${isActive ? activeClasses : inactiveClasses}`;
                  }}
                  onClick={() => setSidebarOpen(false)}
                >
                  {({ isActive }) => (
                    <>
                      <div className={`p-1.5 rounded-md mr-3 transition-all ${
                        isActive ? 'bg-primary-100' : 'bg-primary-50'
                      }`}>
                        <ShieldCheck className={`h-4 w-4 ${isActive ? 'text-primary-600' : 'text-primary-600'}`} />
                      </div>
                      <span>Terms of Service</span>
                    </>
                  )}
                </NavLink>
              </nav>
            </div>
            <div className="flex-shrink-0 border-t border-primary-100/50 p-4">
              <div className="flex items-center space-x-3">
                <UserAvatar size="sm" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">
                    {user?.firstName} {user?.lastName}
                  </p>
                  <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                </div>
                <button
                  onClick={handleLogout}
                  className="p-2 bg-white rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all text-gray-500 hover:text-red-600"
                >
                  <LogOut className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div className="hidden md:flex md:w-64 md:flex-col md:fixed md:inset-y-0">
        <div className="flex-1 flex flex-col min-h-0 bg-neumorph shadow-neomorph m-3 rounded-xl border border-primary-100/30">
          <div className="flex-1 flex flex-col pt-6 pb-4 overflow-y-auto">
            <div className="flex items-center flex-shrink-0 px-5 mb-8">
              <div className="flex items-center space-x-3">
                <img 
                  src="/fastel-logo.png" 
                  alt="Fastel" 
                  className="h-10 w-auto"
                />
                <div>
                  <span className="text-xl font-bold bg-gradient-to-r from-primary-700 via-primary-600 to-primary-400 bg-clip-text text-transparent">
                    Fastel
                  </span>
                  <p className="text-xs text-gray-500 mt-0.5">VTU & Business</p>
                </div>
              </div>
            </div>

            <nav className="flex-1 px-3 space-y-1.5">
              {navigation.map((item) => (
                <NavLink
                  key={item.name}
                  to={item.href}
                  className={({ isActive }) => {
                    const baseClasses = "group flex items-center px-4 py-3 text-sm font-medium rounded-xl transition-all border";
                    const inactiveClasses = "text-gray-600 hover:bg-white hover:shadow-neomorph-hover border-transparent";
                    const activeClasses = item.name === 'Admin'
                      ? "bg-red-50 text-red-600 shadow-neomorph-inset border-red-100"
                      : "bg-primary-50 text-primary-600 shadow-neomorph-inset border-primary-100";
                    
                    return `${baseClasses} ${isActive ? activeClasses : inactiveClasses}`;
                  }}
                >
                  {({ isActive }) => (
                    <>
                      <div className={`p-2 rounded-lg mr-3 transition-all ${
                        item.name === 'Admin' 
                          ? isActive ? 'bg-red-100' : 'bg-red-50'
                          : isActive ? 'bg-primary-100' : 'bg-primary-50'
                      }`}>
                        <item.icon className={`h-4 w-4 ${
                          item.name === 'Admin' ? 'text-red-600' : 'text-primary-600'
                        }`} />
                      </div>
                      <span className="flex-1">{item.name}</span>
                      {item.name === 'Admin' && (
                        <span className="ml-auto bg-red-100 text-red-600 text-xs px-2 py-1 rounded-full font-medium">
                          Admin
                        </span>
                      )}
                    </>
                  )}
                </NavLink>
              ))}

              {/* Divider */}
              <div className="border-t border-primary-100/50 my-3" />

              {/* Support */}
              <NavLink
                to="/support"
                className={({ isActive }) => {
                  const baseClasses = "group flex items-center px-4 py-3 text-sm font-medium rounded-xl transition-all border";
                  const inactiveClasses = "text-gray-600 hover:bg-white hover:shadow-neomorph-hover border-transparent";
                  const activeClasses = "bg-primary-50 text-primary-600 shadow-neomorph-inset border-primary-100";
                  return `${baseClasses} ${isActive ? activeClasses : inactiveClasses}`;
                }}
              >
                {({ isActive }) => (
                  <>
                    <div className={`p-2 rounded-lg mr-3 transition-all ${
                      isActive ? 'bg-primary-100' : 'bg-primary-50'
                    }`}>
                      <HelpCircle className={`h-4 w-4 ${isActive ? 'text-primary-600' : 'text-primary-600'}`} />
                    </div>
                    <span>Help & Support</span>
                  </>
                )}
              </NavLink>

              {/* Legal Pages */}
              <NavLink
                to="/privacy-policy"
                className={({ isActive }) => {
                  const baseClasses = "group flex items-center px-4 py-3 text-sm font-medium rounded-xl transition-all border";
                  const inactiveClasses = "text-gray-600 hover:bg-white hover:shadow-neomorph-hover border-transparent";
                  const activeClasses = "bg-primary-50 text-primary-600 shadow-neomorph-inset border-primary-100";
                  return `${baseClasses} ${isActive ? activeClasses : inactiveClasses}`;
                }}
              >
                {({ isActive }) => (
                  <>
                    <div className={`p-2 rounded-lg mr-3 transition-all ${
                      isActive ? 'bg-primary-100' : 'bg-primary-50'
                    }`}>
                      <FileText className={`h-4 w-4 ${isActive ? 'text-primary-600' : 'text-primary-600'}`} />
                    </div>
                    <span>Privacy Policy</span>
                  </>
                )}
              </NavLink>

              <NavLink
                to="/terms-of-service"
                className={({ isActive }) => {
                  const baseClasses = "group flex items-center px-4 py-3 text-sm font-medium rounded-xl transition-all border";
                  const inactiveClasses = "text-gray-600 hover:bg-white hover:shadow-neomorph-hover border-transparent";
                  const activeClasses = "bg-primary-50 text-primary-600 shadow-neomorph-inset border-primary-100";
                  return `${baseClasses} ${isActive ? activeClasses : inactiveClasses}`;
                }}
              >
                {({ isActive }) => (
                  <>
                    <div className={`p-2 rounded-lg mr-3 transition-all ${
                      isActive ? 'bg-primary-100' : 'bg-primary-50'
                    }`}>
                      <ShieldCheck className={`h-4 w-4 ${isActive ? 'text-primary-600' : 'text-primary-600'}`} />
                    </div>
                    <span>Terms of Service</span>
                  </>
                )}
              </NavLink>
            </nav>
          </div>
          
          {/* User Profile Section */}
          <div className="flex-shrink-0 border-t border-primary-100/50 p-5">
            <div className="flex items-center space-x-3">
              <UserAvatar size="lg" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-800 truncate">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                {user?.role === 'ADMIN' && (
                  <span className="inline-flex items-center mt-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-600">
                    <Shield className="h-3 w-3 mr-1" />
                    Administrator
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="md:pl-64 flex flex-col flex-1">
        {/* Top header */}
        <div className="sticky top-0 z-10 flex-shrink-0 bg-neumorph p-2">
          <div className="bg-white rounded-xl shadow-neomorph border border-primary-100/30">
            <div className="flex h-14 items-center justify-between px-3">
              <div className="flex items-center">
                <button
                  type="button"
                  className="p-2 rounded-lg bg-white shadow-neomorph hover:shadow-neomorph-hover text-gray-500 transition-all md:hidden mr-2"
                  onClick={() => setSidebarOpen(true)}
                >
                  <Menu className="h-5 w-5" />
                </button>
                <div className="flex items-center md:hidden">
                  <img src="/fastel-logo.png" alt="Fastel" className="h-6 w-auto mr-1" />
                  <span className="text-sm font-semibold text-gray-800">
                    Hi, {user?.firstName?.split(' ')[0] || 'User'}!
                  </span>
                </div>
                <div className="hidden md:flex items-center">
                  <div className="w-1 h-6 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-3" />
                  <h1 className="text-lg font-semibold text-gray-800">
                    Welcome back, <span className="text-primary-600">{user?.firstName}</span>!
                  </h1>
                  {user?.role === 'ADMIN' && (
                    <span className="ml-3 inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-red-50 text-red-600 border border-red-100">
                      <Shield className="h-3 w-3 mr-1" />
                      Admin
                    </span>
                  )}
                </div>
              </div>

              <div className="flex items-center space-x-2">
                {/* Wallet Balance */}
                <div className="bg-primary-50 rounded-lg px-2 py-1.5 shadow-neomorph-inset flex items-center space-x-1">
                  <Wallet className="h-3.5 w-3.5 text-primary-500" />
                  <span className="text-xs font-bold text-gray-800">
                    ₦{(wallet?.balance || 0).toLocaleString()}
                  </span>
                </div>

                {/* Notification Bell */}
                <div className="relative">
                  <button
                    className={`relative p-2 rounded-lg transition-all ${
                      notificationsOpen 
                        ? 'bg-primary-50 shadow-neomorph-inset' 
                        : 'bg-white shadow-neomorph hover:shadow-neomorph-hover'
                    }`}
                    onClick={() => setNotificationsOpen(!notificationsOpen)}
                  >
                    {unreadCount > 0 ? (
                      <Bell className="h-5 w-5 text-primary-600" />
                    ) : (
                      <BellOff className="h-5 w-5 text-gray-400" />
                    )}
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 inline-flex items-center justify-center px-1.5 py-0.5 text-[10px] font-bold leading-none text-white bg-gradient-to-br from-red-500 to-red-600 rounded-full shadow-md shadow-red-500/30 min-w-[18px] h-[18px]">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </button>

                  {/* Notifications Dropdown */}
                  {notificationsOpen && (
                    <>
                      <div
                        className="fixed inset-0 z-[100]"
                        onClick={() => setNotificationsOpen(false)}
                      />
                      <div className="fixed left-2 right-2 top-16 w-auto sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-[360px] max-h-[480px] bg-neumorph rounded-xl shadow-neomorph border border-primary-100/30 z-[101] overflow-hidden">
                        {/* Header */}
                        <div className="p-3 border-b border-primary-100/30 flex items-center justify-between bg-white/50">
                          <div className="flex items-center space-x-2">
                            <Bell className="h-4 w-4 text-primary-600" />
                            <span className="text-sm font-semibold text-gray-800">Notifications</span>
                            {unreadCount > 0 && (
                              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                                {unreadCount} unread
                              </span>
                            )}
                          </div>
                          <div className="flex items-center space-x-1">
                            {unreadCount > 0 && (
                              <button
                                onClick={() => {
                                  markAllAsRead();
                                }}
                                className="text-xs text-primary-600 hover:text-primary-700 font-medium px-2 py-1 rounded-lg hover:bg-primary-50 transition-all"
                              >
                                Mark all read
                              </button>
                            )}
                            <button
                              onClick={() => setNotificationsOpen(false)}
                              className="p-1 hover:bg-gray-100 rounded-lg transition-all"
                            >
                              <X className="h-4 w-4 text-gray-400" />
                            </button>
                          </div>
                        </div>

                        {/* Notifications List */}
                        <div className="overflow-y-auto max-h-[360px]">
                          {isLoading ? (
                            <div className="flex items-center justify-center py-8">
                              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                            </div>
                          ) : notifications.length === 0 ? (
                            <div className="text-center py-8 px-4">
                              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-50 shadow-neomorph-inset mb-3">
                                <BellOff className="h-6 w-6 text-gray-400" />
                              </div>
                              <p className="text-sm text-gray-500 font-medium">No notifications</p>
                              <p className="text-xs text-gray-400 mt-1">You're all caught up!</p>
                            </div>
                          ) : (
                            <div className="divide-y divide-primary-100/50">
                              {notifications.map((notification) => (
                                <div
                                  key={notification.id}
                                  className={`p-3 hover:bg-white/70 transition-all cursor-pointer group ${
                                    !notification.read ? 'bg-primary-50/50' : ''
                                  }`}
                                  onClick={() => handleNotificationClick(notification)}
                                >
                                  <div className="flex items-start space-x-3">
                                    {/* Icon */}
                                    <div className={`p-1.5 rounded-lg flex-shrink-0 ${
                                      !notification.read ? 'bg-primary-100' : 'bg-gray-50'
                                    }`}>
                                      {getNotificationIcon(notification.type)}
                                    </div>
                                    
                                    {/* Content */}
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-start justify-between gap-2">
                                        <p className={`text-sm ${!notification.read ? 'font-semibold text-gray-800' : 'text-gray-600'}`}>
                                          {notification.title}
                                        </p>
                                        <button
                                          onClick={(e) => handleNotificationDelete(e, notification.id)}
                                          className="opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-100 rounded-lg transition-all flex-shrink-0"
                                        >
                                          <X className="h-3 w-3 text-gray-400" />
                                        </button>
                                      </div>
                                      <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                                        {notification.message}
                                      </p>
                                      <p className="text-[10px] text-gray-400 mt-1">
                                        {getTimeAgo(notification.createdAt)}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Footer */}
                        {notifications.length > 0 && (
                          <div className="p-2 border-t border-primary-100/30 bg-white/50">
                            <button
                              onClick={() => {
                                setNotificationsOpen(false);
                                navigate('/notifications');
                              }}
                              className="w-full text-center text-xs text-primary-600 hover:text-primary-700 font-medium py-1.5 rounded-lg hover:bg-primary-50 transition-all"
                            >
                              View all notifications
                            </button>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>

                {/* Profile dropdown */}
                <div className="relative">
                  <button
                    className="flex items-center bg-white rounded-lg p-1.5 shadow-neomorph hover:shadow-neomorph-hover transition-all focus:outline-none"
                    onClick={() => setProfileOpen(!profileOpen)}
                  >
                    <UserAvatar size="sm" />
                  </button>

                  {profileOpen && (
                    <>
                      <div
                        className="fixed inset-0 z-[100]"
                        onClick={() => setProfileOpen(false)}
                      />
                      <div className="fixed left-2 right-2 top-16 w-auto sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-64 origin-top-right rounded-xl bg-neumorph shadow-neomorph border border-primary-100/30 z-[101] overflow-hidden">
                        <div className="p-4 border-b border-primary-100/30">
                          <div className="flex items-center space-x-3">
                            <UserAvatar size="lg" />
                            <div>
                              <p className="text-sm font-semibold text-gray-800">
                                {user?.firstName} {user?.lastName}
                              </p>
                              <p className="text-xs text-gray-500">{user?.email}</p>
                            </div>
                          </div>
                        </div>
                        
                        <div className="p-2 space-y-1">
                          {user?.role === 'ADMIN' && (
                            <NavLink
                              to="/admin"
                              className="flex items-center px-3 py-2.5 text-sm text-red-600 hover:bg-white rounded-lg transition-all group"
                              onClick={() => setProfileOpen(false)}
                            >
                              <div className="p-1.5 bg-red-50 rounded-lg mr-3 group-hover:bg-red-100">
                                <Shield className="h-4 w-4" />
                              </div>
                              <span>Admin Panel</span>
                            </NavLink>
                          )}
                          <NavLink
                            to="/profile"
                            className="flex items-center px-3 py-2.5 text-sm text-gray-700 hover:bg-white rounded-lg transition-all group"
                            onClick={() => setProfileOpen(false)}
                          >
                            <div className="p-1.5 bg-primary-50 rounded-lg mr-3 group-hover:bg-primary-100">
                              <User className="h-4 w-4 text-primary-600" />
                            </div>
                            <span>Your Profile</span>
                          </NavLink>
                          <NavLink
                            to="/settings"
                            className="flex items-center px-3 py-2.5 text-sm text-gray-700 hover:bg-white rounded-lg transition-all group"
                            onClick={() => setProfileOpen(false)}
                          >
                            <div className="p-1.5 bg-primary-50 rounded-lg mr-3 group-hover:bg-primary-100">
                              <Settings className="h-4 w-4 text-primary-600" />
                            </div>
                            <span>Settings</span>
                          </NavLink>
                          <button
                            onClick={() => {
                              setProfileOpen(false);
                              handleLogout();
                            }}
                            className="flex items-center w-full px-3 py-2.5 text-sm text-red-600 hover:bg-white rounded-lg transition-all group"
                          >
                            <div className="p-1.5 bg-red-50 rounded-lg mr-3 group-hover:bg-red-100">
                              <LogOut className="h-4 w-4" />
                            </div>
                            <span>Sign out</span>
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 p-4 pb-20 md:pb-5">
          <div className="max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Mobile Bottom Navigation - Fixed with even distribution */}
      <div className="fixed bottom-0 left-0 right-0 md:hidden bg-white border-t border-primary-100/30 shadow-neomorph px-1 py-1 z-50">
        <div className="flex items-center justify-between max-w-md mx-auto">
          {[
            { name: 'Dashboard', href: '/dashboard', icon: Home },
            { name: 'Wallet', href: '/wallet', icon: Wallet },
            { name: 'Services', href: '/services', icon: Zap },
            { name: 'Marketplace', href: '/marketplace', icon: Store },
            { name: 'Connect', href: '/community', icon: Users },
          ].map((item) => (
            <NavLink
              key={item.name}
              to={item.href}
              className={({ isActive }) => {
                const baseClasses = "flex flex-col items-center py-1.5 px-1.5 rounded-lg transition-all flex-1";
                const activeClasses = "text-primary-600 bg-primary-50";
                const inactiveClasses = "text-gray-500";
                return `${baseClasses} ${isActive ? activeClasses : inactiveClasses}`;
              }}
            >
              <item.icon className="h-5 w-5" />
              <span className="text-[10px] mt-0.5">{item.name}</span>
            </NavLink>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MainLayout;